import { useCallback } from 'react';

import type {
  DataTableRowSelectionOptions,
  DataTableRowSelectionProps,
} from './row-selection-types.js';
import { RowSelectionCell, RowSelectionHeader } from './RowSelectionCells.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import {
  DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  DATA_TABLE_ROW_SELECTION_COLUMN_ID,
} from '../../constants.js';
import type {
  ColumnDef,
  Row,
  RowData,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { assignRowMethods } from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';
import type { RowSelectionState } from '../../tanstack-table-core-override/src/index.js';

/**
 * Helper function to identify the ROW_SELECTION_COLUMN
 * @internal
 */
export function isRowSelectionColumnDef<TData extends DataTableRowData>(
  column: ColumnDef<TData>,
): boolean {
  return column.id === ROW_SELECTION_COLUMN.id;
}

/**
 * Column definition for the selection column
 * @internal
 */
export const ROW_SELECTION_COLUMN: ColumnDef<unknown, unknown> = {
  id: DATA_TABLE_ROW_SELECTION_COLUMN_ID,
  alignment: 'left',
  accessorFn: () => '',
  header: ({ table }) => <RowSelectionHeader table={table} />,
  width: 'content',
  minWidth: DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    builtinPlacement: 'prefix',
    cellDeps: (cell) => {
      return [
        cell.row.getIsSelected(),
        cell.row.getIsAllSubRowsSelected(),
        cell.row.getIsSomeSelected(),
        cell.row.getCanSelect(),
        cell.row.getShouldDisableRowSelection(),
      ];
    },
  },
  cell: ({ row }) => <RowSelectionCell row={row} />,
};

const mutateRowIsSelected = <TData extends RowData>(
  selectedRowIds: Record<string, boolean>,
  id: string,
  value: boolean,
  includeChildren: boolean,
  table: Table<TData>,
): void => {
  const row = table.getRow(id, true);

  if (!row.getShouldDisableRowSelection()) {
    if (value) {
      if (
        row.getCanSelect() &&
        !table.getIsRowSelectionLimitReached(selectedRowIds)
      ) {
        selectedRowIds[id] = true;
      }
    } else {
      delete selectedRowIds[id];
    }
  }

  if (
    includeChildren &&
    row.subRows?.length &&
    row.getCanSelectSubRows() &&
    !table.getIsRowSelectionLimitReached(selectedRowIds)
  ) {
    row.subRows.forEach((row) =>
      mutateRowIsSelected(
        selectedRowIds,
        row.id,
        value,
        includeChildren,
        table,
      ),
    );
  }
};

/**
 * @internal
 */
export const DataTableRowSelection: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    let _batchSelectionRowId: string | undefined = undefined;
    let _previousSelectionRange: { start: number; end: number } | undefined =
      undefined;

    table.resetBatchSelection = (event: MouseEvent): void => {
      if (!(_batchSelectionRowId || _previousSelectionRange)) {
        return;
      }

      const checkboxElement = (event.target as HTMLElement).closest(
        '[data-row-selection-checkbox]',
      );

      // Reset batch selection if click was outside of any checkbox
      if (checkboxElement === null) {
        _batchSelectionRowId = undefined;
        _previousSelectionRange = undefined;
        return;
      }

      // If click was on a checkbox, reset if the checkbox belongs to a different table
      if (
        table.getRowVirtualizationContainer()?.contains(checkboxElement) ===
        false
      ) {
        _batchSelectionRowId = undefined;
        _previousSelectionRange = undefined;
      }
    };

    table.getToggleAllLimitedRowsSelectedHandler = () => {
      return (e: unknown) => {
        table.toggleAllLimitedRowsSelected(
          ((e as MouseEvent).target as HTMLInputElement).checked,
        );
      };
    };

    table.getToggleAllLimitedPageRowsSelectedHandler = () => {
      return (e: unknown) => {
        table.toggleAllLimitedPageRowsSelected(
          ((e as MouseEvent).target as HTMLInputElement).checked,
        );
      };
    };

    table.getSelectionCount = () =>
      table.getFilteredSelectedRowModel().flatRows.length;

    // You can access `rows`, `flatRows` and `rowsById` on the row model.
    // Somehow the expanded detail rows are contained in the row model's `rowsById` which should actually not happen
    // since we only adapt `rows` in our `getRowDetailsRowModel`.
    // This leads to an issue when toggling the selection of all rows on the page:
    // TanStack's implementation of `toggleAllPageRowsSelected` iterates over `rows` and would therefore try to select
    // the expanded detail row which is not possible and causes the table to crash.
    // Therefore, we need to overwrite this method and iterate over the `flatRows` to make sure only selectable rows
    // are (de)selected.
    table.toggleAllPageRowsSelected = (value?: boolean) => {
      table.setRowSelection((old) => {
        const resolvedValue = value ?? !table.getIsAllPageRowsSelected();

        const rowSelection: RowSelectionState = { ...old };

        table.getRowModel().flatRows.forEach((row) => {
          mutateRowIsSelected(
            rowSelection,
            row.id,
            resolvedValue,
            false, // we don't need to include children as they are already included in `flatRows`
            table,
          );
        });

        return rowSelection;
      });
    };

    // Override TanStack's implementation to avoid de-selecting disabled rows
    table.toggleAllRowsSelected = (value?: boolean) => {
      table.setRowSelection((old) => {
        const resolvedValue = value ?? !table.getIsAllRowsSelected();

        const rowSelection: RowSelectionState = { ...old };

        table.getPrePaginationRowModel().flatRows.forEach((row) => {
          mutateRowIsSelected(
            rowSelection,
            row.id,
            resolvedValue,
            false, // we don't need to include children as they are already included in `flatRows`
            table,
          );
        });

        return rowSelection;
      });
    };

    table.toggleAllLimitedPageRowsSelected = (value?: boolean) => {
      // Figure out what the target should be, as the "select all" can also be
      // indeterminate. Next steps are
      // false -> true
      // 'indeterminate' -> true
      // true -> false
      const resolvedValue = value ?? !table.getIsAllPageRowsSelected();

      // If we are triggering towards deselect, we can still use the limitless
      // selection
      if (resolvedValue === false) {
        table.toggleAllPageRowsSelected(false);
        return;
      }

      table.setRowSelection((old) => {
        const rowSelection: RowSelectionState = { ...old };
        const limit = table.options.rowSelectionLimit ?? 0;

        // Figure out how many items are already selected.
        const currentSelectionLength = table.getSelectionCount();

        // If there are fewer elements selected than the limit we can fill the
        // selection.
        if (currentSelectionLength < limit) {
          const rows = table.getRowModel().rows;
          let currentSelectionCount = currentSelectionLength;
          for (const row of rows) {
            // If the row cannot be selected, or the element is already selected, we can skip over it
            if (!row.getCanSelect() || row.getIsSelected()) {
              continue;
            }
            rowSelection[row.id] = true;
            currentSelectionCount += 1;

            if (currentSelectionCount >= limit) {
              break;
            }
          }
        }
        return rowSelection;
      });
    };

    table.toggleAllLimitedRowsSelected = (value?: boolean) => {
      // Figure out what the target should be, as the "select all" can also be
      // indeterminate. Next steps are
      // false -> true
      // 'indeterminate' -> true
      // true -> false
      const resolvedValue = value ?? !table.getIsAllPageRowsSelected();

      // If we are triggering towards deselect, we can still use the limitless
      // selection
      if (resolvedValue === false) {
        table.toggleAllRowsSelected(false);
        return;
      }

      table.setRowSelection((old) => {
        const rowSelection: RowSelectionState = { ...old };
        const limit = table.options.rowSelectionLimit ?? 0;

        // Figure out how many items are already selected.
        const currentSelectionLength = table.getSelectionCount();

        // If there are fewer elements selected than the limit we can fill the
        // selection.
        if (currentSelectionLength < limit) {
          const rows = table.getPrePaginationRowModel().flatRows;
          let currentSelectionCount = currentSelectionLength;
          for (const row of rows) {
            // If the row cannot be selected, or the element is already selected, we can skip over it
            if (!row.getCanSelect() || row.getIsSelected()) {
              continue;
            }
            rowSelection[row.id] = true;
            currentSelectionCount += 1;

            if (currentSelectionCount >= limit) {
              break;
            }
          }
        }
        return rowSelection;
      });
    };

    table.getIsRowSelectionLimitReached = (rowSelectionState) => {
      const rowSelection = rowSelectionState || table.getState().rowSelection;
      const rowSelectionLimit = table.options.rowSelectionLimit;
      const selectionCount = Object.keys(rowSelection).length;

      return (
        rowSelectionLimit !== undefined &&
        rowSelectionLimit !== 0 &&
        selectionCount >= rowSelectionLimit
      );
    };

    table.getIsAllPageRowsSelected = () => {
      // if there is a limit for how many rows can be selected, and it is reached, immediately return true
      if (table.getIsRowSelectionLimitReached()) {
        return true;
      }

      return (
        table
          .getPaginationRowModel()
          .flatRows.filter((row) => row.subRows.length === 0)
          // the header check box should be checked, when all rows are either selected or disabled
          .every((row) => row.getIsSelected() || !row.getCanSelect())
      );
    };

    table.getIsAllRowsSelected = () => {
      // if there is a limit for how many rows can be selected, and it is reached, immediately return true
      if (table.getIsRowSelectionLimitReached()) {
        return true;
      }

      return (
        table
          .getPrePaginationRowModel()
          .flatRows.filter((row) => row.subRows.length === 0)
          // the header check box should be checked, when all page rows are either selected or disabled
          .every((row) => row.getIsSelected() || !row.getCanSelect())
      );
    };

    table.getIsSomePageRowsSelected = () => {
      return table
        .getPaginationRowModel()
        .flatRows.filter((row) => row.subRows.length === 0)
        .some((row) => row.getIsSelected());
    };

    table.getIsSomeRowsSelected = () => {
      return table
        .getPrePaginationRowModel()
        .flatRows.filter((row) => row.subRows.length === 0)
        .some((row) => row.getIsSelected());
    };

    table.getIsAllPageRowsSelectionDisabled = () => {
      return table
        .getPaginationRowModel()
        .flatRows.every((row) => row.getShouldDisableRowSelection());
    };

    table.getIsAllRowsSelectionDisabled = () => {
      return table
        .getPrePaginationRowModel()
        .flatRows.every((row) => row.getShouldDisableRowSelection());
    };

    assignRowMethods(table, {
      getShouldDisableRowSelection(this: Row<TData>) {
        const { enableRowSelection } = table.options;
        const isRowSelectionEnabled =
          typeof enableRowSelection === 'function'
            ? enableRowSelection(this)
            : enableRowSelection ?? true;

        // Selected rows should always be deselectable when limits are defined.
        // Otherwise, you could end up in a situation that you have reached a limit
        // but all options that would be deselectable to go under the limit are disabled.
        const hasSelectionLimit = table.options.rowSelectionLimit !== undefined;
        if (hasSelectionLimit && this.getIsSelected()) {
          return false;
        }

        return !isRowSelectionEnabled || table.getIsRowSelectionLimitReached();
      },

      getCanSelect(this: Row<TData>) {
        const isParentRow = !!this.subRows?.length;

        if (isParentRow) {
          return false;
        }

        const { enableRowSelection } = table.options;
        return typeof enableRowSelection === 'function'
          ? enableRowSelection(this)
          : enableRowSelection ?? true;
      },

      toggleSelected(this: Row<TData>, value?: boolean) {
        if (this.getShouldDisableRowSelection()) {
          return;
        }
        const isSelected = this.getIsSelected();

        table.setRowSelection((old) => {
          value = value ?? !isSelected;
          const selectedRowIds = { ...old };

          if (this.getCanSelect() && isSelected === value) {
            return old;
          }

          mutateRowIsSelected(selectedRowIds, this.id, value, true, table);

          return selectedRowIds;
        });
      },

      // Override TanStack's implementation to include selected, but disabled rows
      getIsSomeSelected(this: Row<TData>) {
        const subRows = this.subRows;
        if (!subRows.length) {
          return this.getIsSelected();
        }
        return subRows.some(
          (subRow) => subRow.getIsSelected() || subRow.getIsSomeSelected(),
        );
      },

      // Override TanStack's implementation to include selected, but disabled rows
      getIsAllSubRowsSelected(this: Row<TData>) {
        const subRows = this.subRows;
        if (!subRows.length) {
          return this.getIsSelected();
        }
        for (const subRow of subRows) {
          if (!subRow.getIsAllSubRowsSelected()) {
            return false;
          }
        }
        return true;
      },

      /**
       * Handles row selection/deselection with support for batch selection using Shift+click. If Shift is not pressed,
       * or no previous selection exists, the row is simply selected/deselected and becomes the reference for a possible batch selection.
       * If a batch selection reference exists, and the user selects a row while holding Shift, all rows in between are selected/deselected.
       * If the user then selects another row with the Shift pressed, the previous batch selection is removed, and only the new one is selected.
       * The batch selection reference is reset, once the user clicks anywhere in the table outside the checkboxes.
       */
      batchSelect(this: Row<TData>, value?: boolean, shiftKey?: boolean) {
        const selectSingleRow = () => {
          this.toggleSelected(value);
          _batchSelectionRowId = this.id;
          _previousSelectionRange = undefined;
        };

        // if last selected row is undefined, or if the shift key is not pressed, simply toggle the single row selection
        if (_batchSelectionRowId === undefined || !shiftKey) {
          selectSingleRow();
          return;
        }

        // if the shift key is pressed and there is a previous selection
        // get the start and end index of the batch selection
        const flatRows = table.getRowOrderRowModel().flatRows;
        const firstSelectedIndex = flatRows.findIndex(
          (flatRow) =>
            _batchSelectionRowId && flatRow.id === _batchSelectionRowId,
        );
        const currentSelectedIndex = flatRows.findIndex(
          (flatRow) => flatRow.id === this.id,
        );

        // If the start row cannot be found (e.g. due to changing data) fall back to single selection
        if (firstSelectedIndex === -1) {
          selectSingleRow();
          return;
        }

        const startIndex = Math.min(firstSelectedIndex, currentSelectedIndex);
        const endIndex = Math.max(firstSelectedIndex, currentSelectedIndex);

        const shouldUpdateChildren = (idx: number) => endIndex === idx;

        table.setRowSelection((old) => {
          const rowSelection: RowSelectionState = { ...old };
          // the batch selection checked value will be the same as for the initial selection
          const firstSelectedCheckedValue =
            flatRows[firstSelectedIndex].getIsSelected();

          if (_previousSelectionRange) {
            const start = _previousSelectionRange.start;
            // Clamp end in case the data has shrunk since the last
            const end = Math.min(
              _previousSelectionRange.end,
              flatRows.length - 1,
            );
            // deselect the previous range
            for (let i = start; i <= end; i++) {
              mutateRowIsSelected(
                rowSelection,
                flatRows[i].id,
                false,
                true,
                table,
              );
            }
          }

          // select/deselect current range
          for (let i = startIndex; i <= endIndex; i++) {
            mutateRowIsSelected(
              rowSelection,
              flatRows[i].id,
              firstSelectedCheckedValue,
              shouldUpdateChildren(i),
              table,
            );
          }

          // if there was a selection, save the range
          _previousSelectionRange = firstSelectedCheckedValue
            ? { start: startIndex, end: endIndex }
            : undefined;

          return rowSelection;
        });
      },
    });
  },
};

/**
 * Configuration hook for the DataTable UserActions feature
 * @internal
 */
export function useRowSelection<TData extends DataTableRowData>(
  props: DataTableRowSelectionProps<TData>,
  options: TableOptions<TData>,
): void {
  const configSelectableRows = useDataTableConfigElement('selectableRows');
  // Need to move that into a const here in order to make the typechecks
  // work, because otherwise the selectableRows member could switch.

  // note: for case where the selectableRows prop is an object, it needs
  // to be memoized by the consumer, to avoid updates if it is identical
  const selectableRows = props.selectableRows ?? configSelectableRows;
  // options.enableRowSelection is used as dependency in useBuiltinColumns, therefore we need to cache this function to avoid infinite rerenders
  const enableRowSelectionCallback = useCallback(
    (row: Row<TData>) => {
      // If there is a function defined that will allow for disabling certain
      // row selections, we can apply this one.
      if (
        selectableRows !== undefined &&
        typeof selectableRows !== 'boolean' &&
        selectableRows.disableRowSelection
      ) {
        return !selectableRows.disableRowSelection(row.original);
      }
      return true;
    },
    [selectableRows],
  );

  if (selectableRows !== undefined) {
    options.enableRowSelection = true;
    let rowSelectionMode: DataTableRowSelectionOptions<
      Row<TData>
    >['rowSelectionSelectAllMode'] = 'page';

    // If selectableRows is controlled via a boolean, we can default the options
    if (selectableRows === true || selectableRows === false) {
      options.enableRowSelection = selectableRows;
    } else {
      const { selectAllBehavior, limit } = selectableRows;

      options.enableRowSelection = enableRowSelectionCallback;

      // Change the rowSelectionMode for the _select all_ checkbox if it is defined.
      if (selectAllBehavior) {
        rowSelectionMode = selectAllBehavior;
      }

      // Defines a row selection limit for the maximum count of rows that are
      // selectable.
      options.rowSelectionLimit =
        limit !== undefined && limit >= 0 ? limit : undefined;
    }

    options.enableMultiRowSelection = true;
    options.rowSelectionSelectAllMode = rowSelectionMode;
  } else {
    options.enableRowSelection = false;
  }

  const [selectedRows, setSelectedRows] = useControllableState<
    Record<string, boolean>
  >({
    value: props.selectedRows,
    defaultValue: props.defaultSelectedRows,
    onChange: props.onRowSelectionChange,
  });

  options.state = {
    ...options.state,
    rowSelection: selectedRows ?? {},
  };

  options.onRowSelectionChange = setSelectedRows;
}
